'use client'

import Link from 'next/link'
import { Droplets, Wrench } from 'lucide-react'
import { ArrowRightIcon } from '@/components/brand-kit/icon-sprite'
import { formatCents } from '@/lib/format-utils'
import { describeFitmentCoverage } from '@/lib/filters/fitment-caps'
import { filterPagePath, displayFfn } from '@/lib/parts/fpn'
import { SafeImage } from '@/components/ui/safe-image'
import { FfnChip, FilterBrandTile } from '@/components/filters/filter-brand-tile'

export interface CompatibleFilterItem {
  id: string
  title: string
  brand: string
  model: string
  seo_slug: string
  /** FRIDGE-853 — the Fridge.com Filter Number, what makes each link's accessible name unique. */
  ffn: string | null
  lowest_price_cents: number | null
  is_oem_match: boolean
  /**
   * FRIDGE-853 — OPTIONAL, and in production always absent. The owner ruling of
   * 08/28/2026 took `tags` and `specs_json` out of the appliance page's
   * compatible-filter select because both carried internal provenance strings
   * into the public RSC payload. The interface went on requiring them, so it
   * described data no caller has had since; the `oemSpecs` line below is
   * therefore dormant rather than dead — it renders if a caller ever supplies
   * the fields, and asserts nothing when it cannot.
   */
  tags?: string[]
  specs_json?: Record<string, unknown> | null
  /** FRIDGE-1088 — the cartridge's resolved photos (pickProductImages); the first one is shown. */
  images?: string[] | null
  /** FRIDGE-1088 — the brand's same-origin logo (lib/filters/brand-logos), or null. */
  brand_logo?: string | null
}

/**
 * FRIDGE-1088 — every filter on the site shows a photo, the brand's logo and
 * the FFN. The cartridge's photo, or the designed brand tile when it has none
 * (never the Fridge.com wordmark). Decorative: the row's link names the
 * cartridge.
 */
function FilterThumb({ f, size }: { f: CompatibleFilterItem; size: number }) {
  const photo = f.images?.[0] ?? null
  return (
    <div
      className="flex flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-white"
      style={{ width: size, height: size }}
      aria-hidden="true"
      data-compatible-filter-thumb={photo ? 'photo' : 'tile'}
    >
      {photo ? (
        <SafeImage src={photo} fallbackContent={<FilterBrandTile brand={f.brand} logo={f.brand_logo ?? null} size="xs" />} alt="" width={size * 2} height={size * 2} className="h-full w-full object-contain" brandLogoSrc={f.brand_logo ?? null} />
      ) : (
        <FilterBrandTile brand={f.brand} logo={f.brand_logo ?? null} size="xs" />
      )}
    </div>
  )
}

/** The brand as its logo when it has one, else the brand name in the link blue. */
function BrandLabel({ f }: { f: CompatibleFilterItem }) {
  if (f.brand_logo) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- a brand logo of unknown aspect at text height
      <img src={f.brand_logo} alt={f.brand} loading="lazy" decoding="async" className="inline-block align-middle" style={{ height: '0.95rem', width: 'auto', maxWidth: '5.5rem', objectFit: 'contain' }} />
    )
  }
  return <span className="text-[#0469f0]">{f.brand}</span>
}

interface CompatibleFilterCardProps {
  filters: CompatibleFilterItem[]
  fridgeBrand: string
  filterHubSlug?: string | null
  /**
   * FRIDGE-853 — every compatible filter this appliance has that could render.
   * Defaults to what was passed in, so an un-measured total says nothing.
   */
  totalFilters?: number
  className?: string
}

function formatPrice(cents: number) {
  return formatCents(cents)
}

/** The name a reader sees for one cartridge. Brand and model, never a bare model. */
function filterLabel(f: CompatibleFilterItem): string {
  return [f.brand, f.model].filter(Boolean).join(' ').trim() || f.title
}

/**
 * The accessible name for a link to one cartridge.
 *
 * FRIDGE-853 — brand + model is NOT unique, and the live page proved it: the
 * worst-case appliance carries 29 renderable filters of which only 22 have a
 * model at all, so three model-less Waterdrop rows all reduced to "View filter
 * Waterdrop" pointing at three different URLs — one accessible name, three
 * destinations, the WCAG 2.4.4 failure this was meant to fix.
 *
 * Every one of those 29 rows carries a DISTINCT FFN — the Fridge.com Filter
 * Number, the permanent per-filter identifier — so appending it makes the name
 * unique by construction rather than by luck. This is the same remedy
 * FRIDGE-796 applied to the product cards with the FIN. The visible text is
 * unchanged and still leads the name (WCAG 2.5.3).
 */
function filterLinkName(f: CompatibleFilterItem): string {
  const ffn = displayFfn(f.ffn)
  return `View filter ${filterLabel(f)}${ffn ? ` — ${ffn}` : ''} | Fridge.com`
}

export function CompatibleFilterCard({
  filters,
  fridgeBrand,
  filterHubSlug,
  totalFilters,
  className = "",
}: CompatibleFilterCardProps) {
  if (!filters || filters.length === 0) return null

  // FRIDGE-853 — EVERY row passed in renders exactly once.
  //
  // This card used to show one manufacturer cartridge plus `.slice(0, 3)`
  // alternatives — at most four — while the page's JSON-LD asserted
  // `isRelatedTo` for all ten rows the server had fetched. Six relations the
  // reader could not see is a schema-equals-visible break, and on the 163
  // appliances carrying more than ten sourced filters it also threw away the
  // answer the shopper came for. `rest` is the complement of `oem` rather than
  // "the non-OEM rows", so a second manufacturer cartridge is listed instead of
  // silently dropped, and no row can appear twice.
  const oem = filters.find((f) => f.is_oem_match)
  const rest = filters.filter((f) => f !== oem)
  const oemSpecs = oem?.specs_json as Record<string, string> | null

  const shown = filters.length
  const total = typeof totalFilters === 'number' && totalFilters > shown ? totalFilters : shown
  const coverage = describeFitmentCoverage(shown, total, { one: 'filter', many: 'filters' })

  const heroModelLabel = oem ? `${oem.brand} ${oem.model}` : `${fridgeBrand} Water Filter`

  return (
    <section
      className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${className}`}
    >
      {/* LEFT HALF — Water splash photo with overlay (matches hub hero pattern) */}
      <div className="relative min-h-[200px] md:min-h-[260px] rounded-lg overflow-hidden">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="https://images.unsplash.com/photo-1548839140-29a749e1cf4d?w=1200&q=80&auto=format&fit=crop"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0469f0]/85 via-[#0469f0]/50 to-black/30" />

        {/* Label anchored bottom-left — matches hub hero typography */}
        <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
          <div className="flex items-start gap-3">
            <div className="w-[3px] min-h-[32px] bg-[#ff9951] rounded-full flex-shrink-0 mt-1" />
            <div>
              <p className="text-[10px] font-bold text-white/80 tracking-widest uppercase mb-1">
                Water Filter
              </p>
              {/* FRIDGE-149 §2 — a photo caption, not a section heading: as an
                  <h4> it skipped from the section's H2 straight to level 4. */}
              <p className="text-white text-lg md:text-xl font-bold leading-tight">
                {heroModelLabel}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT HALF — Compatible filters list + links */}
      <div className="bg-white p-5 md:p-6 flex flex-col rounded-lg border border-gray-200">
        <h3 className="text-base font-bold text-gray-900 mb-1 flex items-center gap-2">
          <Droplets className="w-5 h-5 text-[#0469f0]" />
          Compatible Water Filters
        </h3>
        {/* FRIDGE-853 — the honest coverage line. Suppressed when the list is
            showing everything, so a card with three filters never implies a
            longer list exists. */}
        <p className="text-xs text-gray-600 mb-4">
          {coverage ?? `${shown} ${shown === 1 ? 'filter' : 'filters'} we track fit this ${fridgeBrand} refrigerator.`}
        </p>

        {/* OEM card — orange tinted */}
        {oem && (
          <div className="bg-[#ff9951]/[0.06] rounded-lg p-3.5 mb-3">
            <span className="text-[10px] text-[#ff9951] font-bold uppercase tracking-wider">
              OEM (Manufacturer)
            </span>
            <div className="flex items-start gap-3 mt-1.5">
              <FilterThumb f={oem} size={56} />
              <div className="min-w-0">
                <p className="font-bold text-sm text-gray-900">
                  {oem.brand ? <><BrandLabel f={oem} /> {oem.model}</> : filterLabel(oem)}
                </p>
                {displayFfn(oem.ffn) && (
                  <div className="mt-1">
                    <FfnChip ffn={oem.ffn} />
                  </div>
                )}
                {oemSpecs && (
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    {oemSpecs.nsf_certifications || 'NSF Certified'}
                    {oemSpecs.capacity_gallons && ` · ${oemSpecs.capacity_gallons} gal`}
                    {' · Replace every 6 months'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              {oem.lowest_price_cents ? (
                <span className="text-base font-black text-gray-900">
                  {formatPrice(oem.lowest_price_cents)}
                </span>
              ) : (
                <span />
              )}
              {/* FRIDGE-853 — two fixes on one anchor.
                  · It linked to /products/<slug>, which 308s to the filter's
                    real home; the page's own JSON-LD already carried the
                    destination, so the markup and the schema disagreed and
                    every click paid a redirect hop. `filterPagePath` is the
                    one source of that root (never a literal).
                  · "View Filter" is the accessible name a screen reader
                    announces, and the same words repeat on every alternative
                    below pointing somewhere else — one name, many URLs, which
                    is the WCAG 2.4.4 failure FRIDGE-796 pinned on the product
                    cards. The label now names the cartridge; the visible text
                    stays inside it (WCAG 2.5.3). */}
              <Link
                href={filterPagePath(oem.seo_slug)}
                aria-label={filterLinkName(oem)}
                className="bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] font-bold text-xs px-4 py-1.5 rounded-lg transition-all shadow-sm active:scale-95"
              >
                View Filter
              </Link>
            </div>
          </div>
        )}

        {/* The remaining cartridges — every row the server sent that is not the
            hero, so the JSON-LD built from the same array asserts nothing the
            reader cannot see. The heading no longer says "Certified": nothing
            in `product_filter_compatibility` records a certification, and a
            claim the data cannot support does not belong on the page.
            `is_oem_match` DOES record who makes it, so that is what is said. */}
        {rest.length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-2">
              {oem ? 'Other compatible filters' : 'Compatible filters'}
            </p>
            <div className="space-y-1.5">
              {rest.map((filter) => (
                <Link
                  key={filter.id}
                  href={filterPagePath(filter.seo_slug)}
                  aria-label={filterLinkName(filter)}
                  className="flex items-center justify-between p-2.5 bg-gray-50 hover:bg-[#0469f0]/[0.04] rounded-lg transition-colors group"
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <FilterThumb f={filter} size={40} />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-900 truncate">
                        {filter.brand ? <><BrandLabel f={filter} />{filter.model ? ` ${filter.model}` : ''}</> : filterLabel(filter)}
                      </p>
                      {/* FRIDGE-853 — a row with no model reduces to its brand, and
                          the live card stacked three rows all reading "Waterdrop".
                          The accessible name is disambiguated by the FFN, so the
                          visible text is too: a sighted reader gets the same
                          distinguisher as a screen-reader user, not a shorter one.
                          FRIDGE-1088 widened it from model-less rows to EVERY row:
                          the owner's "WHERE ARE LOGOS, FFN ETC AND PHOTOS FIX
                          SITEWIDE" puts the FFN on every filter card. Real-or-Hidden:
                          nothing renders when there is no FFN to print. */}
                      {displayFfn(filter.ffn) && (
                        <p className="text-[11px] text-gray-600 font-mono font-medium">{displayFfn(filter.ffn)}</p>
                      )}
                      {filter.lowest_price_cents ? (
                        <p className="text-sm font-bold text-gray-900">
                          {formatPrice(filter.lowest_price_cents)}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <span
                    aria-hidden="true"
                    className="text-xs text-[#ff9951] font-bold group-hover:translate-x-0.5 transition-transform flex-shrink-0 ml-2"
                  >
                    View →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Footer links — All brand filters + Filter Finder tool */}
        <div className="mt-auto pt-3 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {filterHubSlug && (
            <Link
              href={`/filters/${filterHubSlug}`}
              className="flex items-center justify-between p-2.5 bg-[#0469f0]/[0.04] hover:bg-[#0469f0]/[0.08] rounded-lg transition-colors group"
            >
              <span className="flex items-center gap-2 text-xs font-semibold text-[#0469f0]">
                <Droplets className="w-3.5 h-3.5" />
                All {fridgeBrand} Filters
              </span>
              <ArrowRightIcon className="w-3.5 h-3.5 text-[#0469f0] group-hover:translate-x-0.5 transition-transform" />
            </Link>
          )}
          <Link
            href="/tools/refrigerator-water-filter-finder"
            className="flex items-center justify-between p-2.5 bg-[#0469f0]/[0.04] hover:bg-[#0469f0]/[0.08] rounded-lg transition-colors group"
          >
            <span className="flex items-center gap-2 text-xs font-semibold text-[#0469f0]">
              <Wrench className="w-3.5 h-3.5" />
              Filter Finder Tool
            </span>
            <ArrowRightIcon className="w-3.5 h-3.5 text-[#0469f0] group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  )
}
